import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
} from "react"
import { Search, X } from "lucide-react"
import { PROMPT_CATEGORIES } from "@/config/promptTemplates"
import { Sheet, SheetClose, SheetContent, SheetTitle } from "@/components/ui/sheet"

interface PromptLibraryModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onPromptSelect: (prompt: string) => void
}

const PROMPT_RESULTS = PROMPT_CATEGORIES.flatMap((category) =>
  category.prompts.map((prompt) => ({
    ...prompt,
    categoryId: category.id,
    categoryLabel: category.label,
    categoryIcon: category.icon,
  })),
)

export function PromptLibraryModal({
  open,
  onOpenChange,
  onPromptSelect,
}: PromptLibraryModalProps) {
  const [scope, setScope] = useState("all")
  const [query, setQuery] = useState("")
  const [activeIndex, setActiveIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const resultsRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    setScope("all")
    setQuery("")
    setActiveIndex(0)
  }, [open])

  const normalizedQuery = query.trim().toLocaleLowerCase()
  const filteredPrompts = useMemo(() => PROMPT_RESULTS.filter((prompt) => {
    if (scope !== "all" && prompt.categoryId !== scope) return false
    if (!normalizedQuery) return true
    return [prompt.label, prompt.text, prompt.categoryLabel]
      .some((value) => value.toLocaleLowerCase().includes(normalizedQuery))
  }), [normalizedQuery, scope])

  useEffect(() => {
    setActiveIndex(0)
  }, [normalizedQuery, scope])

  useEffect(() => {
    const activePrompt = resultsRef.current?.querySelector<HTMLElement>(
      '.orca-prompt-library-item[data-active="true"]',
    )
    if (typeof activePrompt?.scrollIntoView === "function") {
      activePrompt.scrollIntoView({ block: "nearest" })
    }
  }, [activeIndex])

  const selectPrompt = (prompt: string) => {
    onPromptSelect(prompt)
    onOpenChange(false)
  }

  const handleSearchKeyDown = (event: ReactKeyboardEvent<HTMLInputElement>) => {
    if (!filteredPrompts.length) return
    if (event.key === "ArrowDown") {
      event.preventDefault()
      setActiveIndex((current) => Math.min(current + 1, filteredPrompts.length - 1))
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      setActiveIndex((current) => Math.max(current - 1, 0))
    } else if (event.key === "Enter") {
      event.preventDefault()
      const prompt = filteredPrompts[activeIndex]
      if (prompt) selectPrompt(prompt.text)
    }
  }

  const portalStyle = useMemo(() => {
    if (typeof document === "undefined") return {} as CSSProperties
    const root = document.querySelector(".orca-engine-4pane")
    if (!root) return {} as CSSProperties
    const computed = getComputedStyle(root)
    const variables = [
      "--orca-bg-card",
      "--orca-bg-elevated",
      "--orca-bg-surface",
      "--orca-text-primary",
      "--orca-text-secondary",
      "--orca-text-muted",
      "--orca-border-subtle",
      "--orca-border-medium",
      "--orca-border-strong",
      "--orca-accent",
      "--orca-accent-soft",
      "--orca-font-body",
    ]
    const style: Record<string, string> = {}
    variables.forEach((variable) => {
      const value = computed.getPropertyValue(variable).trim()
      if (value) style[variable] = value
    })
    return style as CSSProperties
  }, [open])

  const designSystem = typeof document === "undefined"
    ? "default"
    : document.querySelector(".orca-engine-4pane")?.getAttribute("data-design-system") || "default"
  const theme = typeof document === "undefined"
    ? "light"
    : document.querySelector(".orca-engine-4pane")?.getAttribute("data-theme") || "light"
  const resultCountLabel = `${filteredPrompts.length} ${filteredPrompts.length === 1 ? "prompt" : "prompts"}`

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        overlayClassName="orca-prompt-library-backdrop backdrop-blur-none"
        className="orca-prompt-library-modal"
        data-shell-presentation={designSystem}
        data-theme={theme}
        style={portalStyle}
        aria-labelledby="promptLibraryTitle"
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          inputRef.current?.focus()
        }}
        onCloseAutoFocus={(event) => {
          // The launcher owns the return target: closing returns to the helper,
          // while selecting a prompt moves focus into the live composer draft.
          event.preventDefault()
        }}
      >
        <SheetTitle id="promptLibraryTitle" className="orca-prompt-library-sr-only">
          Prompt library
        </SheetTitle>

        <nav className="orca-prompt-library-scopes" aria-label="Prompt category">
          <button
            type="button"
            className={`orca-prompt-library-scope ${scope === "all" ? "active" : ""}`}
            aria-pressed={scope === "all"}
            onClick={() => {
              setScope("all")
              inputRef.current?.focus()
            }}
          >
            All
          </button>
          {PROMPT_CATEGORIES.map((category) => (
            <button
              key={category.id}
              type="button"
              className={`orca-prompt-library-scope ${scope === category.id ? "active" : ""}`}
              aria-pressed={scope === category.id}
              onClick={() => {
                setScope(category.id)
                inputRef.current?.focus()
              }}
            >
              {category.label}
            </button>
          ))}
        </nav>

        <div className="orca-prompt-library-main">
          <div className="orca-prompt-library-query-wrap">
            <Search aria-hidden="true" />
            <input
              ref={inputRef}
              id="promptLibraryInput"
              type="search"
              className="orca-prompt-library-input"
              aria-label="Search prompts"
              placeholder="Search prompts"
              autoComplete="off"
              spellCheck="false"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
          </div>

          <div className="orca-prompt-library-results-head" aria-live="polite">
            <span>{normalizedQuery ? "Search results" : "Available now"}</span>
            <span>{resultCountLabel}</span>
          </div>

          <div
            ref={resultsRef}
            className="orca-prompt-library-results"
            aria-label="Prompt results"
          >
            {filteredPrompts.map((prompt, index) => (
              <button
                key={prompt.id}
                type="button"
                className="orca-prompt-library-item"
                data-active={index === activeIndex}
                onFocus={() => setActiveIndex(index)}
                onMouseEnter={() => setActiveIndex(index)}
                onClick={() => selectPrompt(prompt.text)}
              >
                <span className="orca-prompt-library-item-icon" aria-hidden="true">
                  <span className="material-icons">{prompt.categoryIcon}</span>
                </span>
                <span className="orca-prompt-library-item-text">
                  <strong className="orca-prompt-library-item-title">{prompt.label}</strong>
                  <small className="orca-prompt-library-item-desc">{prompt.text}</small>
                </span>
                <span className="orca-prompt-library-item-type">{prompt.categoryLabel}</span>
              </button>
            ))}

            {filteredPrompts.length === 0 && (
              <div className="orca-prompt-library-empty">
                No prompts match your search.
              </div>
            )}
          </div>
        </div>

        <SheetClose asChild>
          <button
            type="button"
            className="orca-prompt-library-close"
            aria-label="Close prompt library"
          >
            <X aria-hidden="true" />
          </button>
        </SheetClose>
      </SheetContent>
    </Sheet>
  )
}

export default PromptLibraryModal
